import React, { useState, useMemo } from 'react';
import { Typography, Button, Table, Modal, Descriptions, Tag, Space, Card, Row, Col, Statistic } from 'antd';
import { EditOutlined, EyeOutlined, DollarOutlined, ToolOutlined, SettingOutlined } from '@ant-design/icons';
import { formatDate, formatVND } from '../../utils/formatters';
import AssetWorkRecordForm from '../AssetWorkRecordForm';
import WorkRecordDetailModal from '../assetWorkRecord/WorkRecordDetailModal';
import { AssetWorkRecord } from '../../types';
import { useAuth } from '../../hooks/useAuth';

const { Title } = Typography;

interface MaintenanceType {
  id: number;
  name: string;
  description?: string;
}

interface MaintenanceTabProps {
  asset: any;
  maintenanceHistory: AssetWorkRecord[];
  loadingHistory: boolean;
  maintenanceTypes: MaintenanceType[];
  suppliers: any[];
  employees: any[];
  onReload: () => void;
  onSupplierCreated?: () => void;
}

const MaintenanceTab: React.FC<MaintenanceTabProps> = ({
  asset,
  maintenanceHistory,
  loadingHistory,
  maintenanceTypes,
  suppliers,
  employees,
  onReload,
  onSupplierCreated,
}) => {
  const { user } = useAuth();
  const [showWorkRecordForm, setShowWorkRecordForm] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedWorkRecord, setSelectedWorkRecord] = useState<AssetWorkRecord | null>(null);

  // Helper function để render trạng thái tác nghiệp
  const renderWorkStatus = (status: string) => {
    const statusConfig = {
      'pending_execution_approval': { text: 'Chờ duyệt thực hiện', color: '#faad14' },
      'execution_approved': { text: 'Đang thực hiện', color: '#1890ff' },
      'in_progress': { text: 'Đang thực hiện', color: '#1890ff' },
      'pending_completion_approval': { text: 'Chờ duyệt hoàn thành', color: '#722ed1' },
      'completed': { text: 'Hoàn thành', color: '#52c41a' },
      'skipped': { text: 'Bỏ qua', color: '#ff4d4f' },
      'failed': { text: 'Thất bại', color: '#ff4d4f' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { text: status, color: '#666' };

    return (
      <span style={{
        color: config.color,
        fontWeight: '500',
        fontSize: '12px'
      }}>
        {config.text}
      </span>
    );
  };

  // Helper function để mở modal chi tiết
  const handleViewDetail = (record: AssetWorkRecord) => {
    setSelectedWorkRecord(record);
    setShowDetailModal(true);
  };

  // Helper function để mở modal chỉnh sửa
  const handleEdit = (record: AssetWorkRecord) => {
    setSelectedWorkRecord(record);
    setShowEditModal(true);
  };

  // Helper function để tính tuổi tài sản
  const calculateAssetAge = (purchaseDate: string): { years: number; months: number } => {
    const purchase = new Date(purchaseDate);
    const now = new Date();

    let years = now.getFullYear() - purchase.getFullYear();
    let months = now.getMonth() - purchase.getMonth();

    if (months < 0) {
      years--;
      months += 12;
    }

    // Nếu ngày trong tháng chưa đến thì trừ 1 tháng
    if (now.getDate() < purchase.getDate()) {
      months--;
      if (months < 0) {
        years--;
        months += 12;
      }
    }

    return { years, months };
  };

  // Tính toán thống kê
  const statistics = useMemo(() => {
    const stats = {
      totalRecords: maintenanceHistory.length,
      totalCost: 0,
      typeStats: {} as Record<string, { count: number; cost: number }>,
      typeDetails: [] as Array<{ typeName: string; count: number; cost: number }>
    };

    maintenanceHistory.forEach(record => {
      // Đảm bảo cost là number
      let cost = 0;
      if (record.cost !== null && record.cost !== undefined) {
        cost = typeof record.cost === 'string' ? parseFloat(record.cost) || 0 : Number(record.cost) || 0;
      }
      stats.totalCost += cost;

      const typeName = record.maintenance_type_name ||
        maintenanceTypes.find(mt => mt.id === record.maintenance_type_id)?.name ||
        `Loại ${record.maintenance_type_id}`;

      if (!stats.typeStats[typeName]) {
        stats.typeStats[typeName] = { count: 0, cost: 0 };
      }
      stats.typeStats[typeName].count += 1;
      stats.typeStats[typeName].cost += cost;
    });

    // Chuyển đổi thành array để hiển thị
    stats.typeDetails = Object.entries(stats.typeStats).map(([typeName, data]) => ({
      typeName,
      count: data.count,
      cost: data.cost
    }));

    return stats;
  }, [maintenanceHistory, maintenanceTypes]);



  return (
    <div style={{ padding: 24 }}>
      <Title level={5} style={{ marginBottom: 16, textTransform: 'uppercase' }}>
        <SettingOutlined style={{ marginRight: 8 }} />
        Lịch sử tác nghiệp
      </Title>

      {/* Thống kê tổng hợp */}
      {maintenanceHistory.length > 0 && (
        <Card style={{ marginBottom: 16, padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ToolOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
              <span style={{ fontSize: '14px', color: '#666' }}>Tổng số tác nghiệp:</span>
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#1890ff' }}>{statistics.totalRecords}</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', color: '#666' }}>Tổng chi phí:</span>
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#52c41a' }}>{formatVND(statistics.totalCost)}</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', color: '#666' }}>Tuổi tài sản:</span>
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#fa8c16' }}>
                {asset?.purchase_date ? (() => {
                  const age = calculateAssetAge(asset.purchase_date);
                  if (age.years === 0) return `${age.months} tháng`;
                  if (age.months === 0) return `${age.years} năm`;
                  return `${age.years} năm ${age.months} tháng`;
                })() : '—'}
              </span>
            </div>

            {statistics.typeDetails.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '14px', color: '#666' }}>Theo loại:</span>
                {statistics.typeDetails.map((type, index) => (
                  <div key={index} style={{
                    padding: '6px 10px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                    fontSize: '12px',
                    lineHeight: 1.3,
                    minWidth: 110
                  }}>
                    <div style={{ color: '#666', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {type.typeName}: <span style={{ color: '#1890ff' }}>{type.count}</span>
                    </div>
                    <div style={{ color: '#52c41a', fontWeight: 600 }}>{formatVND(type.cost)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {user?.role !== 'employee_user' && (
        <Button type="primary" style={{ marginBottom: 16 }} onClick={() => setShowWorkRecordForm(true)}>
          Ghi nhận tác nghiệp đã hoàn thành
        </Button>
      )}
      <Table
        dataSource={maintenanceHistory}
        loading={loadingHistory}
        rowKey="id"
        size="small"
        pagination={false}
        style={{ marginBottom: 16 }}
        columns={[
          {
            title: 'Mô tả công việc',
            dataIndex: 'work_description',
            key: 'work_description',
            render: (text: string) => text || '—',
            width: 250,
          },
          {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            render: renderWorkStatus,
            width: 100,
          },
          {
            title: 'Ngày thực hiện',
            dataIndex: 'work_date',
            key: 'work_date',
            render: (date: string) => date ? formatDate(date) : '—',
            width: 60,
          },
          {
            title: 'Hành động',
            key: 'action',
            width: 120,
            fixed: 'right',
            render: (_: any, record: AssetWorkRecord) => (
              <Space size="small">
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  size="small"
                  onClick={() => handleViewDetail(record)}
                  title="Xem chi tiết"
                  style={{ color: '#1890ff' }}
                />
                {(user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'manager') && (
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => handleEdit(record)}
                    title="Chỉnh sửa"
                    style={{ color: '#fa8c16' }}
                  />
                )}
              </Space>
            ),
          },
        ]}
      />
      <Modal
        open={showWorkRecordForm}
        title="Ghi nhận tác nghiệp đã hoàn thành"
        onCancel={() => setShowWorkRecordForm(false)}
        footer={null}
        destroyOnHidden
        width={600}
      >
        {asset && (
          <AssetWorkRecordForm
            assetId={asset.id}
            asset={asset}
            maintenanceTypes={maintenanceTypes}
            suppliers={suppliers}
            employees={employees}
            onSuccess={() => {
              setShowWorkRecordForm(false);
              onReload();
            }}
            onCancel={() => setShowWorkRecordForm(false)}
            onSupplierCreated={onSupplierCreated}
          />
        )}
      </Modal>

      {/* Modal chi tiết tác nghiệp */}
      <WorkRecordDetailModal
        visible={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        workRecord={selectedWorkRecord}
      />

      {/* Modal chỉnh sửa tác nghiệp */}
      <Modal
        open={showEditModal}
        title="Chỉnh sửa tác nghiệp"
        onCancel={() => setShowEditModal(false)}
        footer={null}
        destroyOnClose
        width={600}
      >
        {selectedWorkRecord && asset && (
          <AssetWorkRecordForm
            assetId={asset.id}
            asset={asset}
            maintenanceTypes={maintenanceTypes}
            suppliers={suppliers}
            employees={employees}
            onSuccess={() => {
              setShowEditModal(false);
              onReload();
            }}
            onCancel={() => setShowEditModal(false)}
            onSupplierCreated={onSupplierCreated}
            editMode={true}
            initialData={selectedWorkRecord}
          />
        )}
      </Modal>
    </div>
  );
};

export default MaintenanceTab; 